/*
 * --------------------------------------------------------------------------------------
 * FABRIC NARROWING
 * --------------------------------------------------------------------------------------
 *
 * Client-side narrowing for a fabric grid. Four axes:
 *   - colour       multi-select chips, matched against a fabric's colour list
 *   - pattern      multi-select chips
 *   - fabric type  multi-select chips, index only (facet pages have the type fixed)
 *   - name         type-ahead, index only
 *
 * MODEL: OR within an axis, AND across axes. Gray or Brown, in Microvelvet or Faux Fur.
 * This replaces single-select, which manufactured dead ends: of the 132 possible fabric
 * type x colour pairs, 58 return nothing and 54 more return one or two fabrics, so 85%
 * of two-axis combinations were a dead end or close to it. Microvelvet + Gray alone is 4
 * fabrics; Microvelvet or Faux Fur in Gray or Brown is 15. Widening is what a 126-fabric
 * catalogue needs once two axes are in play.
 *
 * HARD RULE, per D-115: this hides tiles and does nothing else. It never writes a query
 * string, never pushes history, and never fetches. Every tile is already in the server
 * HTML. A narrowing control that wrote URLs would manufacture crawlable near-duplicates,
 * which is the defect D-45 rejected.
 *
 * LAYOUT. The controls are a sticky bar above the grid, one button per axis, each opening
 * a popover of chips. Two earlier layouts were tried and replaced: a stacked panel took
 * 452px of a 944px viewport before a fabric showed, and a 260px rail could not fit two
 * pills on a line, so 34 options became 34 rows and 2070px inside a 751px box.
 *
 * The popover STAYS OPEN on selection. It sits in normal flow and pushes the grid rather
 * than covering it: bar 67px plus the tallest popover 173px still leaves ~599px of grid
 * visible at a 944px viewport, so the result changes in view as chips are ticked. An
 * earlier build closed on select to avoid covering the grid; that reasoning does not
 * apply to an in-flow popover and would now fight multi-select.
 *
 * COUNTS. Server Liquid renders the unfiltered count on every chip, so the page is
 * correct with JS off. From first apply() onward every count is recomputed here from the
 * rendered tile set. A chip's number is how many fabrics carry that value AND match the
 * OTHER axes, ignoring its own axis's selections. That is the retail convention and it
 * keeps the number still while you tick through an axis, instead of moving under the
 * cursor. Chips that would return zero are disabled unless already selected, never
 * hidden, so the row does not reflow mid-interaction (debt 82).
 *
 * SCOPE. Default is per section root. A root may declare data-fabric-narrowing-group to
 * bind several roots into one scope. Every current surface has exactly one root.
 *
 * RE-ENTRY GUARD. shopify:section:load fires in the theme editor only. initScope binds a
 * fresh listener per chip, so re-running it over a bound root leaves two closures with
 * independent state. Bound roots are tracked in a WeakSet and skipped. A reloaded section
 * arrives as new DOM, so it binds normally. Assumes no group holds two roots, true on
 * every current surface.
 */
(function () {
  'use strict';

  var boundRoots = new WeakSet();
  var AXES = ['color', 'pattern', 'family'];
  var ATTR = {
    color: 'data-color-value',
    pattern: 'data-pattern-value',
    family: 'data-family-value'
  };

  function slice(nodes) {
    return Array.prototype.slice.call(nodes);
  }

  function initScope(roots) {
    var grid = null;
    var input = null;
    var empty = null;
    var status = null;
    var clearBtn = null;
    var bar = null;

    var chips = { color: [], pattern: [], family: [] };
    var toggles = {};
    var popovers = {};
    var labels = {};

    roots.forEach(function (root) {
      if (!grid) grid = root.querySelector('[data-fabric-grid]');
      if (!input) input = root.querySelector('[data-fabric-search]');
      if (!empty) empty = root.querySelector('[data-fabric-empty]');
      if (!status) status = root.querySelector('[data-fabric-status]');
      if (!clearBtn) clearBtn = root.querySelector('[data-fabric-clear]');
      if (!bar) bar = root.querySelector('[data-fabric-bar]');

      AXES.forEach(function (axis) {
        chips[axis] = chips[axis].concat(slice(root.querySelectorAll('[' + ATTR[axis] + ']')));
        if (!toggles[axis]) toggles[axis] = root.querySelector('[data-fabric-popover-toggle="' + axis + '"]');
        if (!popovers[axis]) popovers[axis] = root.querySelector('[data-fabric-popover="' + axis + '"]');
        if (!labels[axis]) labels[axis] = root.querySelector('[data-fabric-popover-label="' + axis + '"]');
      });
    });

    if (!grid) return;

    roots.forEach(function (root) { boundRoots.add(root); });

    var items = slice(grid.querySelectorAll('.fabric-grid__item')).map(function (el) {
      return {
        el: el,
        pattern: el.getAttribute('data-fabric-pattern') || '',
        family: el.getAttribute('data-fabric-family') || '',
        name: (el.getAttribute('data-fabric-name') || '').toLowerCase(),
        colors: (el.getAttribute('data-fabric-colors') || '')
          .split(' ')
          .filter(function (c) { return c !== ''; })
      };
    });

    // One selected-value list per axis. Empty list means the axis is not filtering.
    var active = { color: [], pattern: [], family: [] };
    var activeQuery = '';

    function itemHas(item, axis, value) {
      if (axis === 'color') return item.colors.indexOf(value) !== -1;
      return item[axis] === value;
    }

    // OR within the axis.
    function axisOk(item, axis) {
      var set = active[axis];
      if (!set.length) return true;
      for (var i = 0; i < set.length; i++) {
        if (itemHas(item, axis, set[i])) return true;
      }
      return false;
    }

    function queryOk(item) { return !activeQuery || item.name.indexOf(activeQuery) !== -1; }

    // AND across axes, optionally ignoring one axis (used for counting).
    function matchExcept(item, skipAxis) {
      if (!queryOk(item)) return false;
      for (var i = 0; i < AXES.length; i++) {
        if (AXES[i] === skipAxis) continue;
        if (!axisOk(item, AXES[i])) return false;
      }
      return true;
    }

    function setCount(chip, n, selected) {
      var span = chip.querySelector('.fabric-chip__count');
      if (span) span.textContent = n;
      chip.disabled = n === 0 && !selected;
    }

    function recount() {
      AXES.forEach(function (axis) {
        chips[axis].forEach(function (chip) {
          var value = chip.getAttribute(ATTR[axis]) || '';
          var selected = value
            ? active[axis].indexOf(value) !== -1
            : active[axis].length === 0;
          var n = 0;
          items.forEach(function (item) {
            if (!matchExcept(item, axis)) return;
            if (value && !itemHas(item, axis, value)) return;
            n++;
          });
          setCount(chip, n, selected);
        });
      });
    }

    function labelsFor(axis) {
      var out = [];
      active[axis].forEach(function (value) {
        chips[axis].forEach(function (chip) {
          if (chip.getAttribute(ATTR[axis]) === value) {
            out.push(chip.getAttribute('data-chip-label') || value);
          }
        });
      });
      return out;
    }

    // "Gray", or "Gray +2" once more than one is picked. Bounded at any number.
    function buttonLabel(axis) {
      var l = labelsFor(axis);
      if (!l.length) return '';
      if (l.length === 1) return l[0];
      return l[0] + ' +' + (l.length - 1);
    }

    function activeCount() {
      var n = activeQuery ? 1 : 0;
      AXES.forEach(function (axis) { n += active[axis].length; });
      return n;
    }

    function closePopovers(exceptAxis) {
      AXES.forEach(function (axis) {
        if (axis === exceptAxis) return;
        if (popovers[axis]) popovers[axis].hidden = true;
        if (toggles[axis]) toggles[axis].setAttribute('aria-expanded', 'false');
      });
    }

    function togglePopover(axis) {
      var pop = popovers[axis];
      if (!pop) return;
      var willOpen = pop.hidden;
      closePopovers(axis);
      pop.hidden = !willOpen;
      if (toggles[axis]) toggles[axis].setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    }

    function renderEmpty(shown) {
      if (!empty) return;
      empty.hidden = shown !== 0;
      if (shown !== 0) return;

      // Naming the combination helps at one or two selections. Beyond that the sentence
      // becomes a list nobody reads, so fall back to the generic form.
      var parts = [];
      var total = 0;
      AXES.forEach(function (axis) {
        var l = labelsFor(axis);
        total += l.length;
        if (l.length) parts.push(l.join(' or '));
      });

      empty.textContent = (total > 0 && total <= 2 && !activeQuery
        ? 'No ' + parts.join(' ') + ' fabrics.'
        : 'No fabrics match your filters.') + ' ';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fabric-grid__clear';
      btn.textContent = 'Show all fabrics';
      btn.addEventListener('click', clearAll);
      empty.appendChild(btn);
    }

    /* After a narrowing change the grid gets shorter, so a visitor scrolled part way
       down can end up below the end of it -- in the footer, looking at nothing. This
       brings them back to the first row, sitting just under the sticky bar.
       ONLY EVER SCROLLS UP. If the visitor is already at or above the target, it
       returns without doing anything, so it can never pull someone further down a
       page they did not ask to move down.
       The open popover is a DESCENDANT of the bar, so the bar's measured height
       already includes it and the first row lands directly beneath it rather than
       behind it. Do not swap this for a fixed bar height.
       Measured inside requestAnimationFrame so the hidden tiles have been laid out
       before anything is read. */
    function reduceMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }
    function scrollToGridTop() {
      if (!grid) return;
      window.requestAnimationFrame(function () {
        var current = window.pageYOffset || document.documentElement.scrollTop || 0;
        var gridTop = grid.getBoundingClientRect().top + current;
        var headroom = 0;
        if (bar) {
          var cs = window.getComputedStyle(bar);
          headroom = bar.getBoundingClientRect().height + (parseFloat(cs.top) || 0);
        }
        var target = gridTop - headroom - 12;
        if (target < 0) target = 0;
        if (current <= target) return;
        try {
          window.scrollTo({ top: target, behavior: reduceMotion() ? 'auto' : 'smooth' });
        } catch (e) {
          window.scrollTo(0, target);
        }
      });
    }
    /* scrollBack is passed only by the interactions that can strand someone: the chips
       and Clear all. It is deliberately NOT passed by the initial apply() at bind time,
       which runs on every page load and would hijack every arrival, nor by the
       type-ahead, which fires per keystroke and would fight the typing. The search
       field lives inside the sticky bar, so it stays on screen and cannot strand. */
    function apply(scrollBack) {
      var shown = 0;
      items.forEach(function (item) {
        var visible = queryOk(item) &&
          axisOk(item, 'color') && axisOk(item, 'pattern') && axisOk(item, 'family');
        item.el.hidden = !visible;
        if (visible) shown++;
      });

      recount();
      renderEmpty(shown);

      AXES.forEach(function (axis) {
        var text = buttonLabel(axis);
        if (labels[axis]) labels[axis].textContent = text;
        if (toggles[axis]) toggles[axis].classList.toggle('is-active', !!text);
      });

      if (clearBtn) clearBtn.hidden = activeCount() === 0;

      if (status) {
        status.textContent = shown === items.length
          ? items.length + ' fabrics'
          : shown + ' of ' + items.length + ' fabrics';
      }

      if (scrollBack) scrollToGridTop();
    }

    function syncChips(axis) {
      chips[axis].forEach(function (chip) {
        var value = chip.getAttribute(ATTR[axis]) || '';
        // The All chip is pressed exactly when nothing in its axis is selected.
        var on = value
          ? active[axis].indexOf(value) !== -1
          : active[axis].length === 0;
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function clearAll() {
      AXES.forEach(function (axis) { active[axis] = []; syncChips(axis); });
      activeQuery = '';
      if (input) input.value = '';
      closePopovers();
      apply(true);
    }

    AXES.forEach(function (axis) {
      chips[axis].forEach(function (chip) {
        chip.addEventListener('click', function () {
          var value = chip.getAttribute(ATTR[axis]) || '';
          if (!value) {
            // All chip: clears its own axis only.
            active[axis] = [];
          } else {
            var at = active[axis].indexOf(value);
            if (at === -1) active[axis].push(value);
            else active[axis].splice(at, 1);
          }
          syncChips(axis);
          apply(true);
          // Popover deliberately left open: multi-select means more picks are likely,
          // and it pushes the grid rather than covering it.
        });
      });

      if (toggles[axis]) {
        toggles[axis].addEventListener('click', function (e) {
          e.stopPropagation();
          togglePopover(axis);
        });
      }
    });

    if (clearBtn) clearBtn.addEventListener('click', clearAll);

    if (input) {
      input.addEventListener('input', function () {
        activeQuery = input.value.trim().toLowerCase();
        apply();
      });
    }

    document.addEventListener('click', function (e) {
      if (bar && bar.contains(e.target)) return;
      closePopovers();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var openAxis = null;
      AXES.forEach(function (axis) {
        if (popovers[axis] && !popovers[axis].hidden) openAxis = axis;
      });
      if (!openAxis) return;
      closePopovers();
      if (toggles[openAxis]) toggles[openAxis].focus();
    });

    AXES.forEach(syncChips);
    closePopovers();
    apply();
  }

  function init() {
    var roots = slice(document.querySelectorAll('[data-fabric-narrowing]'))
      .filter(function (root) { return !boundRoots.has(root); });

    var groups = {};
    var ungrouped = [];

    roots.forEach(function (root) {
      var name = root.getAttribute('data-fabric-narrowing-group') || '';
      if (name) {
        if (!groups[name]) groups[name] = [];
        groups[name].push(root);
      } else {
        ungrouped.push([root]);
      }
    });

    Object.keys(groups).forEach(function (name) { initScope(groups[name]); });
    ungrouped.forEach(initScope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders sections without a full page load. Guarded by boundRoots.
  document.addEventListener('shopify:section:load', init);
})();
